import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { RealtimeChannel } from "@supabase/supabase-js";
import { toast } from "sonner";

export type PortalMode = "serious" | "casual" | "deep" | "anonymous";
export type PortalInviteStatus = "pending" | "accepted" | "declined" | "cancelled";
export type PortalStatus = "active" | "closed";
export type CapsuleType = "video" | "voice" | "prompt";

export type IntroType = "video" | "voice" | "text";

export interface PortalInvite {
  id: string;
  from_user_id: string;
  to_user_id: string;
  mode: PortalMode;
  message: string | null;
  status: PortalInviteStatus;
  created_at: string;
  updated_at: string;
  intro_type: IntroType | null;
  intro_content_url: string | null;
  intro_text: string | null;
  from_user?: {
    id: string;
    name: string;
    photo_url: string | null;
  };
  to_user?: {
    id: string;
    name: string;
    photo_url: string | null;
  };
}

export interface Portal {
  id: string;
  user_a_id: string;
  user_b_id: string;
  mode: PortalMode;
  status: PortalStatus;
  chat_unlocked: boolean;
  created_at: string;
  updated_at: string;
  last_activity_at: string;
  invite_id: string | null;
  other_user?: {
    id: string;
    name: string;
    photo_url: string | null;
  };
}

export interface PortalCapsule {
  id: string;
  portal_id: string;
  sender_user_id: string;
  type: CapsuleType;
  content_url: string | null;
  prompt_text: string | null;
  response_required: boolean;
  responded_to_capsule_id: string | null;
  is_echo: boolean; // True for Echo Capsules (post-unlock voice/photo notes)
  created_at: string;
  sender?: {
    id: string;
    name: string;
    photo_url: string | null;
  };
}

export interface PortalMessage {
  id: string;
  portal_id: string;
  sender_user_id: string;
  text: string;
  created_at: string;
}

export const usePortal = () => {
  const { user } = useAuthContext();
  const [incomingInvites, setIncomingInvites] = useState<PortalInvite[]>([]);
  const [outgoingInvites, setOutgoingInvites] = useState<PortalInvite[]>([]);
  const [portals, setPortals] = useState<Portal[]>([]);
  const [loading, setLoading] = useState(true);

  const channelRef = useRef<RealtimeChannel | null>(null);

  // Fetch incoming invites
  const fetchIncomingInvites = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("portal_invites")
      .select(`
        *,
        from_user:profiles!portal_invites_from_user_id_fkey(id, name, photo_url)
      `)
      .eq("to_user_id", user.id)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (!error && data) {
      setIncomingInvites(data as unknown as PortalInvite[]);
    }
  }, [user]);

  // Fetch outgoing invites
  const fetchOutgoingInvites = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("portal_invites")
      .select(`
        *,
        to_user:profiles!portal_invites_to_user_id_fkey(id, name, photo_url)
      `)
      .eq("from_user_id", user.id)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (!error && data) {
      setOutgoingInvites(data as unknown as PortalInvite[]);
    }
  }, [user]);

  // Fetch user's portals
  const fetchPortals = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("portals")
      .select("*")
      .or(`user_a_id.eq.${user.id},user_b_id.eq.${user.id}`)
      .eq("status", "active")
      .order("last_activity_at", { ascending: false });

    if (!error && data) {
      // Fetch other user info for each portal
      const portalsWithUsers = await Promise.all(
        data.map(async (portal) => {
          const otherUserId = portal.user_a_id === user.id ? portal.user_b_id : portal.user_a_id;
          const { data: otherUser } = await supabase
            .from("profiles")
            .select("id, name, photo_url")
            .eq("id", otherUserId)
            .single();
          
          return { ...portal, other_user: otherUser } as Portal;
        })
      );
      setPortals(portalsWithUsers);
    }
    setLoading(false);
  }, [user]);

  // Upload intro capsule media
  const uploadIntroMedia = useCallback(async (
    type: IntroType,
    content: Blob
  ): Promise<string | null> => {
    if (!user) return null;

    const bucket = type === "video" ? "portal-intros" : "portal-intros";
    const extension = type === "video" ? "webm" : "webm";
    const fileName = `${user.id}/${Date.now()}.${extension}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(fileName, content, {
        contentType: type === "video" ? "video/webm" : "audio/webm",
        cacheControl: "3600",
      });

    if (uploadError) {
      console.error("Upload error:", uploadError);
      return null;
    }

    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(uploadData.path);

    return urlData.publicUrl;
  }, [user]);

  // Send portal invite
  const sendInvite = useCallback(async (
    toUserId: string,
    mode: PortalMode,
    message?: string,
    intro?: { type: IntroType; content: Blob | string } | null
  ): Promise<boolean> => {
    if (!user) return false;

    // Check if invite already exists
    const { data: existingInvite } = await supabase
      .from("portal_invites")
      .select("id")
      .eq("from_user_id", user.id)
      .eq("to_user_id", toUserId)
      .eq("status", "pending")
      .maybeSingle();

    if (existingInvite) {
      toast.info("You already sent an invite to this person");
      return false;
    }

    // Check if portal already exists
    const { data: existingPortal } = await supabase
      .from("portals")
      .select("id")
      .or(`and(user_a_id.eq.${user.id},user_b_id.eq.${toUserId}),and(user_a_id.eq.${toUserId},user_b_id.eq.${user.id})`)
      .eq("status", "active")
      .maybeSingle();

    if (existingPortal) {
      toast.info("You already have an active portal with this person");
      return false;
    }

    // Prepare intro data
    let introType: IntroType | null = null;
    let introContentUrl: string | null = null;
    let introText: string | null = null;

    if (intro) {
      introType = intro.type;
      if (intro.type === "text") {
        introText = intro.content as string;
      } else {
        // Upload media
        introContentUrl = await uploadIntroMedia(intro.type, intro.content as Blob);
        if (!introContentUrl) {
          toast.error("Failed to upload intro");
          return false;
        }
      }
    }

    const { error } = await supabase
      .from("portal_invites")
      .insert({
        from_user_id: user.id,
        to_user_id: toUserId,
        mode,
        message: message || null,
        intro_type: introType,
        intro_content_url: introContentUrl,
        intro_text: introText,
      });

    if (error) {
      toast.error("Couldn't send invite");
      return false;
    }

    toast.success("Portal invite sent! ⚡");
    fetchOutgoingInvites();
    return true;
  }, [user, fetchOutgoingInvites, uploadIntroMedia]);

  // Accept invite
  const acceptInvite = useCallback(async (inviteId: string): Promise<string | null> => {
    if (!user) return null;

    const { data: portalId, error } = await supabase
      .rpc("accept_portal_invite", { _invite_id: inviteId });

    if (error) {
      toast.error("Couldn't accept invite");
      return null;
    }

    toast.success("Portal opened! ✨");
    fetchIncomingInvites();
    fetchPortals();
    return portalId;
  }, [user, fetchIncomingInvites, fetchPortals]);

  // Decline invite
  const declineInvite = useCallback(async (inviteId: string): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from("portal_invites")
      .update({ status: "declined", updated_at: new Date().toISOString() })
      .eq("id", inviteId)
      .eq("to_user_id", user.id);

    if (error) {
      toast.error("Couldn't decline invite");
      return false;
    }

    toast.info("Invite declined");
    fetchIncomingInvites();
    return true;
  }, [user, fetchIncomingInvites]);

  // Cancel outgoing invite
  const cancelInvite = useCallback(async (inviteId: string): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from("portal_invites")
      .update({ status: "cancelled", updated_at: new Date().toISOString() })
      .eq("id", inviteId)
      .eq("from_user_id", user.id);

    if (error) {
      toast.error("Couldn't cancel invite");
      return false;
    }

    fetchOutgoingInvites();
    return true;
  }, [user, fetchOutgoingInvites]);

  // Close portal
  const closePortal = useCallback(async (portalId: string): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from("portals")
      .update({ status: "closed", updated_at: new Date().toISOString() })
      .eq("id", portalId);

    if (error) {
      toast.error("Couldn't close portal");
      return false;
    }

    toast.info("Portal closed");
    fetchPortals();
    return true;
  }, [user, fetchPortals]);

  // Check if portal exists with user
  const getPortalWithUser = useCallback(async (otherUserId: string): Promise<Portal | null> => {
    if (!user) return null;

    const { data } = await supabase
      .from("portals")
      .select("*")
      .or(`and(user_a_id.eq.${user.id},user_b_id.eq.${otherUserId}),and(user_a_id.eq.${otherUserId},user_b_id.eq.${user.id})`)
      .eq("status", "active")
      .maybeSingle();

    return data as Portal | null;
  }, [user]);

  // Setup realtime subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`portal:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "portal_invites",
          filter: `to_user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            toast("New Portal invite! ⚡", {
              description: "Someone wants to connect with you",
            });
          }
          fetchIncomingInvites();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "portals",
        },
        () => {
          fetchPortals();
        }
      )
      .subscribe();

    channelRef.current = channel;
    
    // Initial fetch
    fetchIncomingInvites();
    fetchOutgoingInvites();
    fetchPortals();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [user, fetchIncomingInvites, fetchOutgoingInvites, fetchPortals]);

  return {
    incomingInvites,
    outgoingInvites,
    portals,
    loading,
    sendInvite,
    acceptInvite,
    declineInvite,
    cancelInvite,
    closePortal,
    getPortalWithUser,
    refetch: () => {
      fetchIncomingInvites();
      fetchOutgoingInvites();
      fetchPortals();
    },
  };
};

// Hook for portal detail (capsules + messages)
export const usePortalDetail = (portalId: string) => {
  const { user } = useAuthContext();
  const [portal, setPortal] = useState<Portal | null>(null);
  const [capsules, setCapsules] = useState<PortalCapsule[]>([]);
  const [messages, setMessages] = useState<PortalMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const channelRef = useRef<RealtimeChannel | null>(null);

  // Fetch portal details
  const fetchPortal = useCallback(async () => {
    if (!user || !portalId) return;

    const { data } = await supabase
      .from("portals")
      .select("*")
      .eq("id", portalId)
      .single();

    if (data) {
      const otherUserId = data.user_a_id === user.id ? data.user_b_id : data.user_a_id;
      const { data: otherUser } = await supabase
        .from("profiles")
        .select("id, name, photo_url")
        .eq("id", otherUserId)
        .single();

      setPortal({ ...data, other_user: otherUser } as Portal);
    }
  }, [user, portalId]);

  // Fetch capsules
  const fetchCapsules = useCallback(async () => {
    if (!portalId) return;

    const { data } = await supabase
      .from("portal_capsules")
      .select(`
        *,
        sender:profiles!portal_capsules_sender_user_id_fkey(id, name, photo_url)
      `)
      .eq("portal_id", portalId)
      .order("created_at", { ascending: true });

    if (data) {
      setCapsules(data as unknown as PortalCapsule[]);
    }
  }, [portalId]);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    if (!portalId) return;

    const { data } = await supabase
      .from("portal_messages")
      .select("*")
      .eq("portal_id", portalId)
      .order("created_at", { ascending: true });

    if (data) {
      setMessages(data as PortalMessage[]);
    }
    setLoading(false);
  }, [portalId]);

  // Create capsule
  const createCapsule = useCallback(async (
    type: CapsuleType,
    contentUrl?: string,
    promptText?: string,
    responseRequired?: boolean,
    respondedToCapsuleId?: string
  ): Promise<boolean> => {
    if (!user || !portalId) return false;

    const { error } = await supabase
      .from("portal_capsules")
      .insert({
        portal_id: portalId,
        sender_user_id: user.id,
        type,
        content_url: contentUrl || null,
        prompt_text: promptText || null,
        response_required: responseRequired || false,
        responded_to_capsule_id: respondedToCapsuleId || null,
      });

    if (error) {
      toast.error("Couldn't send capsule");
      return false;
    }

    toast.success("Capsule sent! 💊");
    fetchCapsules();
    fetchPortal(); // Refresh to check if chat unlocked
    return true;
  }, [user, portalId, fetchCapsules, fetchPortal]);

  // Send message (only if chat unlocked)
  const sendMessage = useCallback(async (text: string): Promise<boolean> => {
    if (!user || !portalId || !portal?.chat_unlocked) {
      toast.error("Chat is still locked");
      return false;
    }

    const { error } = await supabase
      .from("portal_messages")
      .insert({
        portal_id: portalId,
        sender_user_id: user.id,
        text,
      });

    if (error) {
      toast.error("Couldn't send message");
      return false;
    }

    fetchMessages();
    return true;
  }, [user, portalId, portal?.chat_unlocked, fetchMessages]);

  // Setup realtime
  useEffect(() => {
    if (!user || !portalId) return;

    const channel = supabase
      .channel(`portal-detail:${portalId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "portal_capsules",
          filter: `portal_id=eq.${portalId}`,
        },
        () => {
          fetchCapsules();
          fetchPortal();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "portal_messages",
          filter: `portal_id=eq.${portalId}`,
        },
        () => {
          fetchMessages();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "portals",
          filter: `id=eq.${portalId}`,
        },
        () => {
          fetchPortal();
        }
      )
      .subscribe();

    channelRef.current = channel;

    fetchPortal();
    fetchCapsules();
    fetchMessages();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [user, portalId, fetchPortal, fetchCapsules, fetchMessages]);

  return {
    portal,
    capsules,
    messages,
    loading,
    createCapsule,
    sendMessage,
    refetch: () => {
      fetchPortal();
      fetchCapsules();
      fetchMessages();
    },
  };
};

// Upload hook for capsule media
export const usePortalUpload = () => {
  const { user } = useAuthContext();
  const [uploading, setUploading] = useState(false);

  const uploadMedia = useCallback(async (file: File, type: "video" | "voice"): Promise<string | null> => {
    if (!user) return null;

    const maxSize = type === "video" ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File must be less than ${type === "video" ? "50MB" : "10MB"}`);
      return null;
    }

    setUploading(true);
    const fileExt = file.name.split(".").pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    const { error } = await supabase.storage
      .from("portal-capsules")
      .upload(fileName, file, { upsert: true });

    if (error) {
      setUploading(false);
      toast.error("Upload failed");
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from("portal-capsules")
      .getPublicUrl(fileName);

    setUploading(false);
    return publicUrl;
  }, [user]);

  return { uploadMedia, uploading };
};
